import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "../auth.service";
import { catchError, defer, from, map, Observable, retry, switchMap, tap, throwError, timer } from "rxjs";
import { MonitoringService } from "../monitoring.service";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { UIErrorFactory } from "../ui-error.factory";
import { SharedMailboxService } from "../shared-mailbox.service";


export interface IGraphService {
  getMessageEml(id: string): Observable<string>;
  deleteMessage(id: string): Observable<string | void>;
  getAttachment<T>(itemId: string, attachmentId: string): Observable<T>;
  findMessageIdByInternetMessageId(internetMessageId: string): Observable<string | null>;
}

@Injectable({
  providedIn: "root",
})
export class GraphService implements IGraphService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService,
    private monitoringService: MonitoringService,
    private readonly uiErrorFactory: UIErrorFactory,
    private readonly sharedMailboxService: SharedMailboxService
  ) { }

  extensionId = "Microsoft.OutlookServices.OpenTypeExtension.SuperOfficeForOutlook.AppointmentLink";
  private static readonly TRANSIENT_STATUSES = new Set<number>([408, 429, 503, 504]);
  private static readonly MAX_RETRY_DELAY_MS = 10_000;
  private static readonly RETRY_JITTER_MS = 300;

  getMessageEml(id: string): Observable<string> {
    const url = `https://graph.microsoft.com/v1.0/${this.sharedMailboxService.mailboxSegment}/messages/${encodeURIComponent(id)}/$value`;

    return this.withAuth((token) =>
      this.httpClient.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "text",
      })
    );
  }

  deleteMessage(id: string): Observable<string> {
    const url = `https://graph.microsoft.com/v1.0/${this.sharedMailboxService.mailboxSegment}/messages/${encodeURIComponent(id)}/move`;

    return this.withAuth((token) =>
      this.httpClient.post(
        url,
        { destinationId: "deletedItems" },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "text",
        }
      )
    );
  }

  /**
   * Resolves the Graph (REST) message id for an item by its internetMessageId.
   * Used as a fallback when the Office.js item exposes an empty itemId
   * (observed with S/MIME signed emails on Outlook for Mac).
   * Returns null when no message matches.
   */
  findMessageIdByInternetMessageId(internetMessageId: string): Observable<string | null> {
    const filter = `internetMessageId eq '${internetMessageId.replace(/'/g, "''")}'`;
    const url = `https://graph.microsoft.com/v1.0/${this.sharedMailboxService.mailboxSegment}/messages?$filter=${encodeURIComponent(filter)}&$select=id&$top=2`;

    return this.withAuth((token) => this.get<{ value: { id: string }[] }>(url, token)).pipe(
      map((response) => {
        const matches = response?.value ?? [];
        if (matches.length > 1) {
          // Copies in multiple folders (e.g. Sent + Inbox) share an internetMessageId; content is identical.
          this.monitoringService.logCustomEvent("INFO: Multiple messages matched internetMessageId, using first", { count: matches.length });
        }
        return matches.length > 0 ? matches[0].id : null;
      })
    );
  }

  getAttachment<T>(itemId: string, attachmentId: string): Observable<T> {
    const url = `https://graph.microsoft.com/v1.0/${this.sharedMailboxService.mailboxSegment}/messages/${encodeURIComponent(itemId)}/attachments/${encodeURIComponent(
      encodeURIComponent(attachmentId)
    )}`;

    return this.withAuth((token) => this.get<T>(url, token)).pipe(
      tap((attachment: T) => {
        const sizeBytes = (attachment as any)?.size || 0;
        const sizeKB = sizeBytes / 1024;
        this.monitoringService.logMetric("AttachmentSizeKB", parseFloat(sizeKB.toFixed(2)));
      })
    );
  }

  /**
   * Executes a function that performs an authenticated HTTP operation.
   * Retries transient Graph errors (408/429/503/504) with capped exponential backoff + jitter,
   * honors Retry-After when present, and routes 401s to the CAE challenge flow.
   */
  private withAuth<T>(httpCall: (token: string) => Observable<T>): Observable<T> {
    return defer(() => from(this.authService.getAccessToken(false))).pipe(
      // Token is resolved once here; retries below replay only the HTTP call with the same token.
      switchMap((token) =>
        defer(() => httpCall(token)).pipe(
          retry({
            count: 2,
            delay: (error: HttpErrorResponse, attempt) => {
              if (GraphService.TRANSIENT_STATUSES.has(error.status)) {
                const delayMs = this.getRetryDelayMs(error, attempt);
                this.monitoringService.logCustomEvent(`INFO: Graph transient ${error.status} - retry ${attempt}/2 in ${delayMs}ms`);
                return timer(delayMs);
              }
              return throwError(() => error);
            }
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handleAuthError(error, httpCall);
        }
        if (GraphService.TRANSIENT_STATUSES.has(error.status)) {
          this.monitoringService.logCustomEvent(`ERROR: Graph API call failed after retries - status ${error.status}`);
        } else {
          this.monitoringService.logCustomEvent(`ERROR: Graph API call failed - status ${error.status}`);
        }
        return throwError(() => error);
      })
    );
  }

  private getRetryDelayMs(error: HttpErrorResponse, attempt: number): number {
    const retryAfter = error.headers?.get("Retry-After");
    let baseDelayMs: number;
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      baseDelayMs = !isNaN(seconds) && seconds >= 0 ? seconds * 1000 : Math.pow(2, attempt - 1) * 1000;
    } else {
      baseDelayMs = Math.pow(2, attempt - 1) * 1000; // 1s, 2s
    }
    // Cap so a hostile/large Retry-After can't freeze the task pane, then add jitter to avoid synchronized retries.
    const cappedMs = Math.min(baseDelayMs, GraphService.MAX_RETRY_DELAY_MS);
    const jitterMs = Math.floor(Math.random() * GraphService.RETRY_JITTER_MS);
    return cappedMs + jitterMs;
  }

  private handleAuthError<T>(error: HttpErrorResponse, httpCall: (token: string) => Observable<T>): Observable<T> {
    const header = error.headers?.get("www-authenticate");
    const isCaeChallenge = error.status === 401 && !!header;

    if (!isCaeChallenge) {
      this.monitoringService.logCustomEvent("INFO: withAuthentication - Non-CAE 401, attempting silent token refresh");
      this.authService.clearAccessTokenCache();

      return defer(() => from(this.authService.getAccessToken(false))).pipe(
        switchMap((token) => httpCall(token)),
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status !== 401) {
            this.monitoringService.logCustomEvent("ERROR: withAuthentication - Non-CAE 401 retry failed with unexpected error", { status: err.status });
            const customError = this.uiErrorFactory.createGraphTransientFailure(this.monitoringService.getSessionId(), err);
            return throwError(() => customError);
          }
          this.monitoringService.logCustomEvent("ERROR: withAuthentication - Non-CAE 401, retry did not resolve auth", { status: err?.status });
          const customError = this.uiErrorFactory.createSOffice365APIAuthInterrupted(this.monitoringService.getSessionId(), err);
          return throwError(() => customError);
        })
      );
    }

    const challenges = this.parseChallenges(header!);
    const claimsChallenge = atob(challenges.claims);

    this.monitoringService.logCustomEvent(challenges.error_description);
    this.monitoringService.logCustomEvent("INFO: CAE detected - retrying with claims challenge");

    return defer(() => from(this.authService.getAccessToken(false, claimsChallenge))).pipe(
      switchMap((token) => httpCall(token)),
      catchError((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          this.monitoringService.logCustomEvent("INFO: CAE - Interaction required - retrying interactively");
          return defer(() => from(this.authService.getAccessToken(true, claimsChallenge))).pipe(switchMap((token) => httpCall(token)));
        }

        const customError = this.uiErrorFactory.createCaeFailure(
          this.monitoringService.getSessionId(), error);

        return throwError(() => customError);
      })
    );
  }

  // helper function to parse the www-authenticate header
  // source https://learn.microsoft.com/en-us/entra/identity-platform/app-resilience-continuous-access-evaluation?tabs=JavaScript
  private parseChallenges(header: string): { [key: string]: string } {
    const schemeSeparator = header.indexOf(" ");
    const challenges = header.substring(schemeSeparator + 1).split(",");
    const challengeMap = {};

    challenges.forEach((challenge) => {
      const [key, value] = challenge.split("=");
      challengeMap[key.trim()] = window.decodeURI(value.replace(/['"]+/g, ""));
    });
    return challengeMap;
  }

  public arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
    const sizeInMB = buffer.byteLength / (1024 * 1024);

    if (sizeInMB < 5) {
      // Fast in-memory conversion with chunking
      let binary = "";
      const bytes = new Uint8Array(buffer);
      const chunkSize = 0x8000; // 32KB

      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
      }
      return Promise.resolve(btoa(binary));
    }

    // Safe fallback for bigger payloads
    return new Promise((resolve, reject) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(",")[1]); // strip "data:...;base64,"
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private get<T>(url: string, accessToken: string) {
    return this.httpClient.get<T>(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    });
  }

}
